/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import styles from './styles.module.scss';
import ScrollableContent from '../../components/ScrollableContent';
import DataLoader from '../../components/DataLoader';
import CoverBanner from '../../components/CoverBanner';
import TrackListHeader from '../../components/TrackListHeader';
import TrackListRow from '../../components/TrackListRow';
import ActionButtons from '../../components/ActionButtons';
import ActionButton from '../../components/ActionButton';
import PeopleAggregation from '../../components/PeopleAggregation';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import WorklistBasicForm from '../../forms/WorklistBasicForm';
import actions from '../../redux/actions';
import { getIsPlaying } from '../../redux/reducers/playerQueue';
import { getSelectedTrackIds, getExpandedTrackIds } from '../../redux/reducers/media';
import { getTrackSearchParams } from '../../redux/reducers/trackSearch';
import { getSessionUsageId } from '../../redux/reducers/usages';
import { getWorklists, getWorklist1, getWorklist2 } from '../../redux/reducers/worklists';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';
import Button from '../../components/Button';
import InputSelect from '../../components/InputSelect';

import type {
  ReduxState,
  Fetchable,
  Media,
  SimplifiedMedia,
  TrackMedia,
  SimplifiedWorklist,
  SimplifiedTrack,
  ReactRouterProps,
  Translate,
  SearchParams,
  TrackSearchParams,
  Track,
} from '../../types';

type Props = ReactRouterProps & {
  worklists: Fetchable<SimplifiedWorklist[]>,
  worklist1: Fetchable<SimplifiedWorklist>,
  worklist2: Fetchable<SimplifiedWorklist>,
  sessionUsageId: Fetchable<number>,
  isPlaying: boolean,
  currentTrackId: number,
  trackSearchParams: TrackSearchParams,
  media: Fetchable<Media>,
  pageScroll: number,
  selectedTracks: {
    [trackId: number]: boolean,
  },
  selectedTrackIds: number[],
  expandedTracks: {
    [trackId: number]: boolean,
  },
  expandedTrackIds: number[],
  playTracks: typeof actions.playTracks,
  downloadTracks: typeof actions.downloadTracks,
  askQuotation: typeof actions.askQuotation,
  createCartItemsFromTracks: typeof actions.createCartItemsFromTracks,
  createWorklistItems: typeof actions.createWorklistItems,
  saveWorklist: typeof actions.saveWorklist,
  performTrackSearch: typeof actions.performTrackSearch,
  displayMessage: typeof actions.displayMessage,
  fetchMedia: typeof actions.fetchMedia,
  mediaSetPageScroll: typeof actions.mediaSetPageScroll,
  mediaExpandTrack: typeof actions.mediaExpandTrack,
  mediaToggleSelectTracks: typeof actions.mediaToggleSelectTracks,
  mediaToggleExpandSelectedTracks: typeof actions.mediaToggleExpandSelectedTracks,
  t: Translate,
};

type State = {
  isCreateWorklistModalVisible: boolean,
  createWorklistWithItemsAction: (any => mixed) | null,
  isSelectWorklistModalVisible: boolean,
  worklists: SimplifiedWorklist[],
  selectWorklistTracks: Track[],
  selectWorklistSessionUsageId: number,
  selectWorklistSelectedId: number,
};

const mapStateToProps = (state: ReduxState) => {
  return {
    worklists: getWorklists(state),
    worklist1: getWorklist1(state),
    worklist2: getWorklist2(state),
    sessionUsageId: getSessionUsageId(state),
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    trackSearchParams: getTrackSearchParams(state),
    media: state.media.media,
    pageScroll: state.media.pageScroll,
    selectedTracks: state.media.selectedTracks,
    selectedTrackIds: getSelectedTrackIds(state),
    expandedTracks: state.media.expandedTracks,
    expandedTrackIds: getExpandedTrackIds(state),
  };
};

class MediaContainer extends React.Component<Props, State> {
  state = {
    isCreateWorklistModalVisible: false,
    createWorklistWithItemsAction: null,
    isSelectWorklistModalVisible: false,
    worklists: [],
    selectWorklistTracks: [],
    selectWorklistSessionUsageId: 0,
    selectWorklistSelectedId: 0,
  };

  scrollContainer = React.createRef();
  pageScroll: number = 0;

  componentDidMount() {
    const mediaId = parseInt(this.props.match.params.mediaId);
    if (!this.props.media.data || this.props.media.data.id !== mediaId) {
      this.props.fetchMedia(mediaId);
    }
    if (this.scrollContainer.current) {
      this.scrollContainer.current.scrollTop = this.props.pageScroll;
    }
  }

  componentWillUnmount() {
    this.props.mediaSetPageScroll(this.pageScroll);
  }

  _handlePageScroll = (event: any) => {
    const element = event.target;
    const pageScroll = element.scrollTop;
    this.pageScroll = pageScroll;
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _requestCreateWorklistWithItems = (tracks: SimplifiedTrack[], usageId: number) => {
    const { props } = this;
    const { saveWorklist, createWorklistItems, t } = props;
    const createWorklistWithItemsAction = (name: string, description: string) =>
      saveWorklist(
        {
          name: name || t('WORKLIST'),
          description: description || t('NO_DESCRIPTION'),
          usage: protonesUtils.getUsageUpdateParamsFromObject({}),
        },
        async worklist => {
          await createWorklistItems(tracks, worklist, usageId);
          // protonesUtils.goTo(props, protonesUtils.routes.worklists.get(worklist.id));
        }
      );
    this._showCreateWorklistModal(createWorklistWithItemsAction);
  };

  _showCreateWorklistModal = createWorklistWithItemsAction => {
    this.setState({ isCreateWorklistModalVisible: true, createWorklistWithItemsAction });
  };

  _dismissCreateWorklistModal = () => {
    this.setState({ isCreateWorklistModalVisible: false, createWorklistWithItemsAction: null });
  };

  _handleCreateWorklistModalSubmit = worklistParams => {
    const { createWorklistWithItemsAction } = this.state;
    const { name, description } = worklistParams;
    this._dismissCreateWorklistModal();
    createWorklistWithItemsAction(name, description);
  };

  _handleTrackSearch = (params: SearchParams) => {
    const { performTrackSearch, trackSearchParams } = this.props;
    performTrackSearch(_.merge({}, trackSearchParams, params));
    protonesUtils.goTo(this.props, protonesUtils.routes.tracks.find());
  };

  _showSelectWorklistModal = (worklists, tracks, sessionUsageId) => {
    this.setState({
      isSelectWorklistModalVisible: true,
      worklists,
      selectWorklistTracks: tracks,
      selectWorklistSessionUsageId: sessionUsageId,
    });
  };

  _dismissSelectWorklistModal = () => {
    this.setState({ isSelectWorklistModalVisible: false });
  };

  _setSelectWorklistSelectedId = selectWorklistSelectedId => {
    this.setState({ selectWorklistSelectedId });
  };

  _renderActionButtons = (tracks: TrackMedia[]) => {
    const {
      displayMessage,
      createWorklistItems,
      downloadTracks,
      sessionUsageId,
      worklist1,
      worklist2,
      worklists,
      t,
    } = this.props;
    const downloadable = tracks.every(track => track.downloadable === true);
    return (
      <ActionButtons
        // $FlowFixMe
        download={{
          fetchable: downloadable,
          fetching: false,
          data: () =>
            downloadable
              ? downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
        worklist1={{
          fetching: worklist1.fetching || sessionUsageId.fetching,
          error: worklist1.error || sessionUsageId.error,
          data: {
            ...worklist1.data,
            // $FlowFixMe
            onClick: worklist1.data
              ? // $FlowFixMe
                () => createWorklistItems(tracks, worklist1.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL1'), true),
          },
        }}
        worklist2={{
          fetching: worklist2.fetching || sessionUsageId.fetching,
          error: worklist2.error || sessionUsageId.error,
          data: {
            ...worklist2.data,
            // $FlowFixMe
            onClick: worklist2.data
              ? // $FlowFixMe
                () => createWorklistItems(tracks, worklist2.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL2'), true),
          },
        }}
        worklists={{
          fetching: worklists.fetching || sessionUsageId.fetching,
          error: worklists.error || sessionUsageId.error,
          data: {
            ...worklists.data,
            onClick: () =>
              this._showSelectWorklistModal(worklists.data, tracks, sessionUsageId.data),
          },
          tooltip: t('ADD_TO') + ' ' + t('WORKLIST'),
        }}
        // worklists={{
        //   fetching: worklists.fetching || sessionUsageId.fetching,
        //   error: worklists.error || sessionUsageId.error,
        //   data: worklists.data && [
        //     {
        //       name: t('CREATE'),
        //     },
        //     {
        //       id: 0,
        //       name: t('NEW_WORKLIST'),
        //       // $FlowFixMe
        //       onClick: () => this._requestCreateWorklistWithItems(tracks, sessionUsageId.data),
        //     },
        //     {
        //       name: t('ADD_TO'),
        //     },
        //     ...(worklists.data || []).map(worklist => ({
        //       ...worklist,
        //       onClick: () => createWorklistItems(tracks, worklist, sessionUsageId.data),
        //     })),
        //   ],
        // }}
      />
    );
  };

  _renderMediaDetails = media => {
    const { t } = this.props;
    const altPerformers = protonesUtils.getMediaPerformersLabels(media);
    let description = media.categories
      ? media.categories.map(_ => _.get(_, 'description')).join(' ')
      : '';
    if (media.description) {
      description += `\n${t('CATALOG')}: ${media.catalogNumber}`;
    }

    return (
      <CoverBanner
        title={protonesUtils.getMediaLabel(media)}
        subtitle={
          <PeopleAggregation
            people={media.performers}
            isExpanded={true}
            emptyText={altPerformers.length > 0 ? altPerformers.join(', ') : 'Nessun interprete'}
          />
        }
        description={description}
        imageSrc={protonesUtils.getMediaCoverUrl(media) || ''}
        details={protonesUtils.getMediaDetails(media)}
      />
    );
  };

  _renderData = data => {
    const { isPlaying, currentTrackId, playTracks } = this.props;
    const media = data;
    const isSelectedAllExpanded = this.props.selectedTrackIds.every(id =>
      this.props.expandedTrackIds.includes(id)
    );
    const isAllSelected = media.tracks
      .map(t => t.id)
      .every(id => this.props.selectedTrackIds.includes(id));
    const isSomeSelected = this.props.selectedTrackIds.length > 0;
    const selectedTracks: Track[] = (media.tracks || []).filter(track =>
      _.includes(this.props.selectedTrackIds, track.id)
    );
    return (
      <div className={styles.content}>
        <div className={styles.close}>
          <ActionButton iconName={'close'} onClick={this.props.history.goBack} />
        </div>
        {this._renderMediaDetails(media)}
        <Table.Table>
          <TrackListHeader
            showCheckbox={true}
            showTrackNumber={true}
            isSelectable={true}
            isSelected={isAllSelected}
            isPlayerVisible={selectedTracks.length > 0}
            onPlayClick={() => playTracks(selectedTracks)}
            onDetailsExpand={this.props.mediaToggleExpandSelectedTracks}
            isDetailsExpanded={isSelectedAllExpanded}
            showActionButtons={isSomeSelected}
            onCheckboxClick={() => this.props.mediaToggleSelectTracks(media.tracks.map(t => t.id))}
            actionButtons={this._renderActionButtons(selectedTracks)}
          />
          {media.tracks.map((track: TrackMedia, index) => {
            const isSelected = !!this.props.selectedTracks[track.id];
            const isExpanded = !!this.props.expandedTracks[track.id];
            return (
              <TrackListRow
                key={`${index}-${track.id}`}
                showTrackNumber={true}
                track={track}
                medias={[media]}
                isSelectable={true}
                isSelected={isSelected}
                isCurrentTrack={currentTrackId === track.id}
                isPlaying={isPlaying}
                isLoading={false}
                isDetailsExpanded={isExpanded}
                onPerformerClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onAuthorClick={person =>
                  this._handleTrackSearch({
                    text: person.publicName,
                    flags: { people: true },
                  })
                }
                onMediaClick={this._handleMediaClick}
                onCheckboxClick={() => this.props.mediaToggleSelectTracks([track.id])}
                onPlayClick={() => playTracks([track])}
                actionButtons={this._renderActionButtons([track])}
                onDetailsExpand={isExpanded => this.props.mediaExpandTrack(track.id, isExpanded)}
              />
            );
          })}
        </Table.Table>
      </div>
    );
  };

  render() {
    const { media, t, createWorklistItems } = this.props;
    const {
      isCreateWorklistModalVisible,
      isSelectWorklistModalVisible,
      worklists,
      selectWorklistTracks,
      selectWorklistSessionUsageId,
      selectWorklistSelectedId,
    } = this.state;
    return (
      <div className={styles.root}>
        <ScrollableContent onScroll={this._handlePageScroll} ref={this.scrollContainer}>
          <DataLoader
            loading={media.fetching}
            error={media.error}
            data={media.data}
            render={this._renderData}
          />
        </ScrollableContent>
        {isCreateWorklistModalVisible && (
          <Modal
            title={stringUtils.capitalizeString(t('NEW_WORKLIST'))}
            onDismiss={this._dismissCreateWorklistModal}
          >
            <WorklistBasicForm save={this._handleCreateWorklistModalSubmit} />
          </Modal>
        )}
        {isSelectWorklistModalVisible && (
          <Modal
            title={t('ADD_TO') + ' ' + t('WORKLIST')}
            onDismiss={this._dismissSelectWorklistModal}
          >
            <InputSelect
              label={t('ADD_TO')}
              title={t('ADD_TO')}
              onChange={e => this._setSelectWorklistSelectedId(e.target.value)}
            >
              <option key={0} value={''} />
              {worklists.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </InputSelect>
            <div style={{ marginTop: '2em', display: 'flex', justifyContent: 'space-around' }}>
              <span />
              <Button
                text={t('CANCEL')}
                onClick={this._dismissSelectWorklistModal}
                disabled={false}
                appearance={'flat'}
              />
              <Button
                text={t('NEW_WORKLIST')}
                onClick={() => {
                  this._requestCreateWorklistWithItems(
                    selectWorklistTracks,
                    selectWorklistSessionUsageId
                  );
                  this._dismissSelectWorklistModal();
                }}
                disabled={false}
                appearance={'flat'}
              />
              <Button
                text={t('ADD')}
                onClick={() => {
                  const worklist = worklists.find(
                    worklist => worklist.id === parseInt(selectWorklistSelectedId)
                  );
                  createWorklistItems(selectWorklistTracks, worklist, selectWorklistSessionUsageId);
                  this._dismissSelectWorklistModal();
                }}
                disabled={selectWorklistSelectedId === 0}
                appearance={'primary'}
              />
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(MediaContainer)));
